import * as React from 'react';
import { ApiHatasi, api } from '@/lib/api';
import { konaklamaVergisiOrani } from '@hotelcms/shared';

/**
 * Misafire acik online rezervasyon sayfasi (/rezervasyon).
 *
 * Tasarim kaynagi: Claude Design — "otelcms Online Rezervasyon.dc.html".
 * Tasarimdaki oteller, odalar ve fiyatlar ureticiydi; buradaki her sey
 * tesisin kendi verisinden gelir:
 *   GET  /api/public/rezervasyon/tesis      -> otel adi, adres, telefon
 *   GET  /api/public/rezervasyon/musaitlik  -> gercek kalan oda + tarife
 *   POST /api/public/rezervasyon            -> rezervasyon (fiyat sunucuda)
 *
 * Tasarimdan bilerek ayrilan yerler ekranda da durustur:
 *   - Ustu cizili "eski fiyat" ve "%10 dogrudan rezervasyon indirimi" YOK:
 *     karsiligi olmayan bir indirim gostermek yaniltici olur.
 *   - Kredi karti adimi YOK: bagli bir odeme saglayicisi yok, kart bilgisi
 *     istenmez. Odeme otelde alinir.
 *   - "Onay e-postasi gonderildi" denmez: e-posta saglayicisi henuz bagli
 *     degil (bkz. queue/notify-consumer.ts). Kod ekranda verilir.
 */

interface Tesis {
  ad: string;
  slug: string;
  adres: string | null;
  telefon: string | null;
  eposta: string | null;
  paraBirimi: string;
  vergiOrani: number;
}

interface OdaSecenegi {
  roomTypeId: string;
  ad: string;
  aciklama: string | null;
  maksYetiskin: number | null;
  maksCocuk: number | null;
  kalanOda: number;
  pansiyon: string;
  gecelikOrtalama: number;
  araToplam: number;
  vergi: number;
  toplam: number;
  paraBirimi: string;
}

interface Sonuc {
  kod: string;
  araToplam: number;
  vergi: number;
  toplam: number;
  paraBirimi: string;
  onlineCheckinUrl: string | null;
  /** Sunucu e-posta gonderebiliyor mu? Ekran buna gore konusur. */
  epostaGonderiliyor: boolean;
}

/** Sistemdeki pansiyon kodlari (pansiyonSchema): OB, BB, HB, FB, AI. */
const PANSIYON_ADI: Record<string, string> = {
  OB: 'Sadece oda',
  BB: 'Kahvaltı dahil',
  HB: 'Yarım pansiyon',
  FB: 'Tam pansiyon',
  AI: 'Her şey dahil',
};

const ADIMLAR = ['Tarihler', 'Oda', 'Bilgiler', 'Onay'];

const isoGun = (d: Date) => d.toISOString().slice(0, 10);
const gunEkle = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoGun(d);
};
const geceSay = (a: string, b: string) =>
  Math.max(0, Math.round((new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86_400_000));
const gunEtiket = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', weekday: 'short' });

/** Vergi oranini yuzde olarak yazar: 0.01 -> "1", 0.005 -> "0,5". */
function yuzde(oran: number): string {
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(oran * 100);
}

function para(tutar: number, birim: string) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: birim, maximumFractionDigits: 0 }).format(tutar);
}

/**
 * Yuksekligi ust pencereye bildirir (gomulu mod).
 *
 * iframe kendi icerigine gore buyuyemez; boyu disaridan verilmek zorunda.
 * Bu yuzden icerik her degistiginde olculen boyu `postMessage` ile
 * gonderiyoruz, `embed.js` de iframe'in yuksekligini ayarliyor.
 */
function yukseklikBildir(el: HTMLElement | null) {
  if (!el || window.parent === window) return () => {};
  const gonder = () => {
    window.parent.postMessage(
      { tip: 'otelcms:yukseklik', yukseklik: Math.ceil(el.getBoundingClientRect().height) },
      // Ust pencerenin adresini bilmiyoruz; gonderilen tek sey bir sayi.
      '*',
    );
  };
  gonder();
  const gozcu = new ResizeObserver(gonder);
  gozcu.observe(el);
  return () => gozcu.disconnect();
}

export function OnlineRezervasyon({ gomulu = false }: { gomulu?: boolean } = {}) {
  const kokRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => yukseklikBildir(kokRef.current), [gomulu]);

  const [adim, setAdim] = React.useState(0);
  const [tesis, setTesis] = React.useState<Tesis | null>(null);

  const bugun = React.useMemo(() => isoGun(new Date()), []);
  const [giris, setGiris] = React.useState(() => gunEkle(isoGun(new Date()), 1));
  const [cikis, setCikis] = React.useState(() => gunEkle(isoGun(new Date()), 3));
  const [yetiskin, setYetiskin] = React.useState(2);
  const [cocuk, setCocuk] = React.useState(0);

  const [odalar, setOdalar] = React.useState<OdaSecenegi[]>([]);
  const [yukleniyor, setYukleniyor] = React.useState(false);
  const [secili, setSecili] = React.useState<OdaSecenegi | null>(null);

  const [ad, setAd] = React.useState('');
  const [soyad, setSoyad] = React.useState('');
  const [eposta, setEposta] = React.useState('');
  const [telefon, setTelefon] = React.useState('');
  const [not, setNot] = React.useState('');
  const [onlineCheckin, setOnlineCheckin] = React.useState(true);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);
  const [sonuc, setSonuc] = React.useState<Sonuc | null>(null);
  const [kopyalandi, setKopyalandi] = React.useState(false);

  React.useEffect(() => {
    api<Tesis>('/api/public/rezervasyon/tesis')
      .then(setTesis)
      .catch(() => undefined);
  }, []);

  const gece = geceSay(giris, cikis);
  // Konaklama vergisi orani sabit degildir (bkz. shared/konaklama-vergisi.ts):
  // 1.5.2026'dan beri %1. Ekranda asla sabit yazilmaz, sunucudan gelir.
  const vergiOrani = tesis?.vergiOrani ?? konaklamaVergisiOrani(new Date().toISOString().slice(0, 10));
  const kisiEtiket = `${yetiskin} yetişkin${cocuk > 0 ? `, ${cocuk} çocuk` : ''}`;

  async function ara(e?: React.FormEvent) {
    e?.preventDefault();
    if (gece < 1) return;
    setAdim(1);
    setYukleniyor(true);
    setHata(null);
    setSecili(null);
    try {
      const veri = await api<{ odalar: OdaSecenegi[] }>(
        `/api/public/rezervasyon/musaitlik?girisTarihi=${giris}&cikisTarihi=${cikis}&yetiskin=${yetiskin}&cocuk=${cocuk}`,
      );
      setOdalar(veri.odalar);
    } catch (err) {
      setOdalar([]);
      setHata(err instanceof ApiHatasi ? err.message : 'Müsaitlik alınamadı. Lütfen tekrar deneyin.');
    } finally {
      setYukleniyor(false);
    }
  }

  async function onayla(e: React.FormEvent) {
    e.preventDefault();
    if (!secili) return;
    if (!ad.trim() || !soyad.trim() || !eposta.trim()) {
      setHata('Ad, soyad ve e-posta zorunlu.');
      return;
    }
    setGonderiliyor(true);
    setHata(null);
    try {
      const cevap = await api<Sonuc>('/api/public/rezervasyon', {
        method: 'POST',
        body: JSON.stringify({
          girisTarihi: giris,
          cikisTarihi: cikis,
          roomTypeId: secili.roomTypeId,
          yetiskin,
          cocuk,
          ad: ad.trim(),
          soyad: soyad.trim(),
          eposta: eposta.trim(),
          telefon: telefon.trim() || null,
          not: not.trim() || null,
          onlineCheckin,
        }),
      });
      setSonuc(cevap);
      setAdim(3);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Rezervasyon tamamlanamadı.');
      setGonderiliyor(false);
    }
  }

  function bastanBasla() {
    setAdim(0);
    setSecili(null);
    setSonuc(null);
    setAd('');
    setSoyad('');
    setEposta('');
    setTelefon('');
    setNot('');
    setHata(null);
    setGonderiliyor(false);
  }

  return (
    <div
      ref={kokRef}
      className={gomulu ? 'flex flex-col bg-transparent' : 'flex min-h-dvh flex-col bg-zemin'}
    >
      {!gomulu && <Baslik tesis={tesis} />}

      <main
        className={
          gomulu
            ? 'flex w-full flex-1 flex-col gap-5 p-4'
            : 'mx-auto flex w-full max-w-[1100px] flex-1 flex-col gap-6 px-6 pb-16 pt-7'
        }
      >
        <Adimlar adim={adim} />

        {adim === 0 && (
          <Arama
            tesisAdi={tesis?.ad ?? null}
            bugun={bugun}
            giris={giris}
            cikis={cikis}
            yetiskin={yetiskin}
            cocuk={cocuk}
            gece={gece}
            onGiris={(v) => {
              setGiris(v);
              if (v >= cikis) setCikis(gunEkle(v, 1));
            }}
            onCikis={setCikis}
            onYetiskin={setYetiskin}
            onCocuk={setCocuk}
            onAra={ara}
          />
        )}

        {adim === 1 && (
          <Odalar
            ozet={`${gunEtiket(giris)} → ${gunEtiket(cikis)} · ${gece} gece · ${kisiEtiket}`}
            gece={gece}
            vergiOrani={vergiOrani}
            odalar={odalar}
            yukleniyor={yukleniyor}
            hata={hata}
            onGeri={() => setAdim(0)}
            onSec={(o) => {
              setSecili(o);
              setHata(null);
              setAdim(2);
            }}
          />
        )}

        {adim === 2 && secili && (
          <Bilgiler
            oda={secili}
            gece={gece}
            giris={giris}
            cikis={cikis}
            kisiEtiket={kisiEtiket}
            vergiOrani={vergiOrani}
            ad={ad}
            soyad={soyad}
            eposta={eposta}
            telefon={telefon}
            not={not}
            onlineCheckin={onlineCheckin}
            hata={hata}
            gonderiliyor={gonderiliyor}
            setAd={setAd}
            setSoyad={setSoyad}
            setEposta={setEposta}
            setTelefon={setTelefon}
            setNot={setNot}
            setOnlineCheckin={setOnlineCheckin}
            onGeri={() => setAdim(1)}
            onOnayla={onayla}
          />
        )}

        {adim === 3 && sonuc && secili && (
          <Onay
            sonuc={sonuc}
            oda={secili}
            gece={gece}
            vergiOrani={vergiOrani}
            giris={giris}
            cikis={cikis}
            eposta={eposta}
            tesis={tesis}
            kopyalandi={kopyalandi}
            onKopyala={() => {
              navigator.clipboard?.writeText(sonuc.kod).catch(() => undefined);
              setKopyalandi(true);
              setTimeout(() => setKopyalandi(false), 1500);
            }}
            onYeni={bastanBasla}
          />
        )}
      </main>

      {gomulu ? (
        <p className="px-4 pb-4 text-center text-[12px] text-[#8a93ad]">
          {tesis?.ad ?? 'Tesis'} adına{' '}
          <a href="https://otelcms.com" target="_blank" rel="noreferrer" className="font-semibold text-marka-600">
            otelcms
          </a>{' '}
          ile güvenli rezervasyon
        </p>
      ) : (
        <footer className="border-t border-cizgi bg-white">
          <div className="mx-auto flex w-full max-w-[1100px] flex-wrap justify-between gap-3 px-6 py-[18px] text-[13px] text-[#8a93ad]">
            <span>© {new Date().getFullYear()} {tesis?.ad ?? ''}</span>
            <span className="flex items-center gap-1.5">
              Altyapı <a href="https://otelcms.com" className="font-semibold text-marka-600">otelcms</a>
            </span>
          </div>
        </footer>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- ust bant */

function Baslik({ tesis }: { tesis: Tesis | null }) {
  const bas = (tesis?.ad ?? '·').trim().charAt(0).toLocaleUpperCase('tr-TR');
  const host = typeof location !== 'undefined' ? location.host : '';
  return (
    <header className="border-b border-cizgi bg-white">
      <div className="mx-auto flex w-full max-w-[1100px] flex-wrap items-center gap-x-6 gap-y-4 px-6 py-4">
        <a href="/" className="flex items-center gap-3 text-lacivert">
          <span className="grid size-10 place-items-center rounded-xl bg-lacivert text-base font-bold tracking-[-.02em] text-white">
            {bas}
          </span>
          <span className="flex flex-col">
            <span className="text-[17px] font-bold tracking-[-.02em]">{tesis?.ad ?? 'Yükleniyor…'}</span>
            <span className="text-xs text-gri">{[tesis?.adres, host].filter(Boolean).join(' · ')}</span>
          </span>
        </a>
        <div className="ml-auto flex flex-wrap items-center gap-4 text-sm text-gri">
          <span className="flex items-center gap-2 whitespace-nowrap">
            <span className="relative size-2">
              <span className="absolute inset-0 rounded-full bg-yesil" />
              <span className="absolute inset-0 rounded-full border-[1.5px] border-yesil [animation:ring_1.8s_ease-out_infinite]" />
            </span>
            Canlı müsaitlik
          </span>
          {tesis?.telefon && (
            <a href={`tel:${tesis.telefon.replace(/\s/g, '')}`} className="whitespace-nowrap font-semibold text-lacivert">
              {tesis.telefon}
            </a>
          )}
        </div>
      </div>
    </header>
  );
}

function Adimlar({ adim }: { adim: number }) {
  return (
    <div className="flex items-center gap-2 [animation:rise_.5s_both] sm:gap-0">
      {ADIMLAR.map((etiket, i) => (
        <div
          key={etiket}
          className={
            'flex items-center gap-2.5 ' +
            // Dar sutunda (otelin kenar cubugu, telefon) dort etiket birden
            // sigmaz; yalnizca bulunulan adimin adi yazilir, o da tum bos
            // alani alir. Genis ekranda dordu de esit paylasir.
            (i === adim ? 'min-w-0 flex-1' : 'flex-none sm:min-w-0 sm:flex-1')
          }
        >
          <span
            className={
              'grid size-[30px] flex-none place-items-center rounded-full border-[1.5px] text-[13px] font-bold transition-all ' +
              (i <= adim ? 'border-lacivert bg-lacivert text-white' : 'border-cizgi bg-white text-[#8a93ad]')
            }
          >
            {i < adim ? '✓' : i + 1}
          </span>
          <span
            className={
              'truncate text-sm font-semibold ' +
              (i === adim ? 'inline ' : 'hidden sm:inline ') +
              (i <= adim ? 'text-lacivert' : 'text-[#8a93ad]')
            }
          >
            {etiket}
          </span>
          {i < 3 && (
            <span
              className={
                'mx-3 hidden h-[1.5px] flex-1 transition-colors sm:block ' +
                (i < adim ? 'bg-lacivert' : 'bg-cizgi')
              }
            />
          )}
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------- adim 1 */

function Arama(p: {
  tesisAdi: string | null;
  bugun: string;
  giris: string;
  cikis: string;
  yetiskin: number;
  cocuk: number;
  gece: number;
  onGiris: (v: string) => void;
  onCikis: (v: string) => void;
  onYetiskin: (v: number) => void;
  onCocuk: (v: number) => void;
  onAra: (e?: React.FormEvent) => void;
}) {
  return (
    <>
      <section className="relative overflow-hidden rounded-[24px] bg-lacivert p-6 text-white [animation:rise_.6s_.05s_cubic-bezier(.2,.7,.2,1)_both] sm:p-11">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.05) 1px,transparent 1px)',
            backgroundSize: '36px 36px',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-40 size-[420px] rounded-full"
          style={{ background: 'radial-gradient(circle,rgba(31,79,216,.6),transparent 70%)' }}
        />
        <div className="relative grid items-center gap-8 sm:grid-cols-[minmax(280px,1fr)_auto]">
          <div className="flex flex-col gap-3.5">
            <span className="text-[13px] font-bold uppercase tracking-[.12em] text-mavi-acik">Online rezervasyon</span>
            <h1 className="text-[30px] font-bold leading-[1.02] tracking-[-.035em] sm:text-[46px]">
              Odanızı doğrudan ayırtın
            </h1>
            <p className="max-w-[52ch] text-base leading-relaxed text-[#b6c2e2]">
              Müsaitlik ve fiyatlar {p.tesisAdi ? `${p.tesisAdi}'in` : 'tesisin'} kendi sisteminden canlı gelir; aracı
              yok. Rezervasyon anında onaylanır, isterseniz kimliğinizi önceden yükleyip resepsiyonda beklemezsiniz.
            </p>
          </div>
          <OtelCizimi />
        </div>

        <form
          onSubmit={p.onAra}
          className="relative mt-7 grid gap-2 rounded-[18px] bg-white p-2 text-lacivert sm:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]"
        >
          <AramaAlani etiket="Giriş">
            <input
              type="date"
              min={p.bugun}
              value={p.giris}
              onChange={(e) => p.onGiris(e.target.value)}
              className="border-0 bg-transparent p-0 text-[15px] font-semibold text-lacivert outline-none"
            />
          </AramaAlani>
          <AramaAlani etiket="Çıkış" ipucu={p.gece > 0 ? `${p.gece} gece` : 'Geçersiz'}>
            <input
              type="date"
              min={gunEkle(p.giris, 1)}
              value={p.cikis}
              onChange={(e) => p.onCikis(e.target.value)}
              className="border-0 bg-transparent p-0 text-[15px] font-semibold text-lacivert outline-none"
            />
          </AramaAlani>
          <AramaAlani etiket="Yetişkin">
            <select
              value={p.yetiskin}
              onChange={(e) => p.onYetiskin(Number(e.target.value))}
              className="border-0 bg-transparent p-0 text-[15px] font-semibold text-lacivert outline-none"
            >
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </AramaAlani>
          <AramaAlani etiket="Çocuk">
            <select
              value={p.cocuk}
              onChange={(e) => p.onCocuk(Number(e.target.value))}
              className="border-0 bg-transparent p-0 text-[15px] font-semibold text-lacivert outline-none"
            >
              {[0, 1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </AramaAlani>
          <button
            type="submit"
            disabled={p.gece < 1}
            className="flex items-center justify-center gap-2 rounded-xl bg-marka-600 px-5 py-3.5 text-[15px] font-semibold text-white transition hover:bg-lacivert disabled:opacity-40"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="7" cy="7" r="5" />
              <path d="M11 11l3 3" />
            </svg>
            Müsaitlik ara
          </button>
        </form>
      </section>

      <div className="grid gap-3.5 [animation:rise_.6s_.15s_both] sm:grid-cols-2 lg:grid-cols-4">
        {(
          [
            ['Aracısız fiyat', 'Otelin kendi tarifesi; komisyon eklenmez.', 'M9 1.5l2.2 4.6 5 .7-3.6 3.5.9 5L9 12.9l-4.5 2.4.9-5L1.8 6.8l5-.7z'],
            ['Anında onay', 'Müsaitlik canlı hesaplanır; çift rezervasyon olmaz.', 'M3 9l4 4 8-8'],
            ['Online check-in', 'Kimliğinizi önceden yükleyin, girişte sadece anahtarı alın.', 'M2 4h14v10H2zM5 9a2 2 0 100-4 2 2 0 000 4M10 7h4M10 10h3'],
            ['Ödeme otelde', 'Kart bilgisi istenmez, saklanmaz.', 'M9 3a6 6 0 100 12A6 6 0 009 3M9 6v3l2 2'],
          ] as const
        ).map(([t, b, icon]) => (
          <div key={t} className="flex items-start gap-3 rounded-2xl border border-cizgi bg-white p-4">
            <span className="grid size-9 flex-none place-items-center rounded-[10px] bg-mavi-zemin text-marka-600">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={icon} />
              </svg>
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-lacivert">{t}</span>
              <span className="text-[13px] leading-relaxed text-gri">{b}</span>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function AramaAlani({ etiket, ipucu, children }: { etiket: string; ipucu?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 rounded-xl px-3.5 py-2.5 transition-colors hover:bg-zemin">
      <span className="flex items-baseline justify-between gap-2 text-[11px] font-bold uppercase tracking-[.08em] text-gri">
        {etiket}
        {ipucu && <span className="tracking-normal text-marka-600">{ipucu}</span>}
      </span>
      {children}
    </label>
  );
}

function OtelCizimi() {
  return (
    <svg viewBox="0 0 320 200" className="w-full max-w-[320px] justify-self-center" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <g style={{ animation: 'bob 5s ease-in-out infinite' }}>
        <rect x="60" y="40" width="200" height="120" rx="14" fill="#fff" />
        <path d="M80 66h44M80 90h60M80 114h50" stroke="#c9d3ea" strokeWidth="6" />
        {[
          [150, 62, 90, '#1f4fd8', 0.5],
          [160, 86, 60, '#1f4fd8', 1],
          [150, 110, 80, '#f2b632', 1.5],
        ].map(([x, y, w, renk, gecikme]) => (
          <g key={`${y}`} style={{ transformBox: 'fill-box', transformOrigin: 'left center', animation: `bar .8s ${gecikme}s cubic-bezier(.2,.8,.2,1) both` }}>
            <rect x={x as number} y={y as number} width={w as number} height="9" rx="4.5" fill={renk as string} />
          </g>
        ))}
        <rect x="80" y="132" width="160" height="14" rx="7" fill="#0b1530" />
      </g>
      <circle cx="40" cy="170" r="14" stroke="#8fb0ff" strokeWidth="2" />
      <circle cx="40" cy="170" r="5" stroke="#8fb0ff" strokeWidth="2" />
      <path d="M54 170h40M80 170v8M90 170v6" stroke="#8fb0ff" strokeWidth="2" />
      <path d="M270 30l8 8M300 30l-8 8" stroke="#f2b632" strokeWidth="2" style={{ animation: 'blink 1.6s infinite' }} />
    </svg>
  );
}

/* --------------------------------------------------------------- adim 2 */

function Odalar(p: {
  ozet: string;
  gece: number;
  vergiOrani: number;
  odalar: OdaSecenegi[];
  yukleniyor: boolean;
  hata: string | null;
  onGeri: () => void;
  onSec: (o: OdaSecenegi) => void;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 [animation:rise_.4s_both]">
        <h2 className="text-2xl font-bold tracking-[-.02em] text-lacivert">Müsait odalar</h2>
        <span className="text-sm text-gri">{p.ozet}</span>
        <button
          onClick={p.onGeri}
          className="ml-auto rounded-full border border-cizgi bg-white px-3.5 py-2 text-[13px] font-semibold text-lacivert transition hover:border-lacivert"
        >
          Tarihleri değiştir
        </button>
      </div>

      {p.yukleniyor ? (
        <div className="flex flex-col gap-3.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[150px] rounded-[20px] bg-[linear-gradient(90deg,#eef1f8_25%,#f7f9fd_50%,#eef1f8_75%)] bg-[length:200%_100%] [animation:shimmer_1.4s_infinite]"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      ) : p.hata ? (
        <p className="rounded-[20px] border border-cizgi bg-white px-6 py-10 text-center text-sm text-kirmizi">{p.hata}</p>
      ) : p.odalar.length === 0 ? (
        <div className="rounded-[20px] border border-cizgi bg-white px-6 py-12 text-center">
          <p className="text-[17px] font-semibold text-lacivert">Bu tarihlerde müsait oda yok</p>
          <p className="mx-auto mt-2 max-w-[46ch] text-sm leading-relaxed text-gri">
            Başka tarih deneyebilir ya da doğrudan oteli arayabilirsiniz — telefonla hâlâ yer bulunabilir.
          </p>
          <button
            onClick={p.onGeri}
            className="mt-5 rounded-full bg-marka-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-lacivert"
          >
            Tarihleri değiştir
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          {p.odalar.map((o, i) => (
            <div
              key={o.roomTypeId}
              className="grid items-center gap-5 rounded-[20px] border-[1.5px] border-cizgi bg-white p-5 transition hover:-translate-y-0.5 hover:border-marka-600 hover:shadow-[0_22px_44px_-30px_rgba(11,21,48,.35)] md:grid-cols-[200px_minmax(0,1fr)_auto] [animation:popin_.45s_cubic-bezier(.2,.8,.2,1)_both]"
              style={{ animationDelay: `${i * 90}ms` }}
            >
              <div
                className="relative flex h-[130px] items-end overflow-hidden rounded-[14px] p-3"
                style={{ background: ['linear-gradient(160deg,#1f4fd8,#8fb0ff)', 'linear-gradient(160deg,#0f7a3d,#5fd48f)', 'linear-gradient(160deg,#0b1530,#1f4fd8)'][i % 3] }}
              >
                <svg viewBox="0 0 200 130" className="absolute inset-0 size-full" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M20 96h160M30 96V66h60v30M110 96V56h60v40M38 74h12M38 84h12M120 66h14M120 78h14M140 66h14M140 78h14" />
                  <circle cx="160" cy="30" r="12" fill="rgba(242,182,50,.9)" stroke="none" />
                  <path d="M0 112q20-8 40 0t40 0 40 0 40 0 40 0" stroke="rgba(255,255,255,.9)" />
                </svg>
                {o.maksYetiskin !== null && (
                  <span className="relative rounded-full bg-lacivert/55 px-2 py-[3px] text-[11px] font-bold text-white">
                    {o.maksYetiskin} yetişkin{o.maksCocuk ? ` + ${o.maksCocuk} çocuk` : ''}
                  </span>
                )}
              </div>

              <div className="flex min-w-0 flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h3 className="text-[19px] font-bold tracking-[-.01em] text-lacivert">{o.ad}</h3>
                  {o.kalanOda <= 2 && (
                    <span className="flex items-center gap-1.5 rounded-full bg-[#fff5dc] px-2.5 py-[3px] text-[11px] font-bold text-[#b9840f]">
                      <span className="size-1.5 rounded-full bg-sari [animation:blink_1.2s_infinite]" />
                      Son {o.kalanOda} oda
                    </span>
                  )}
                </div>
                {o.aciklama && <p className="text-sm leading-relaxed text-gri">{o.aciklama}</p>}
                <div className="flex flex-wrap gap-2">
                  {[PANSIYON_ADI[o.pansiyon] ?? o.pansiyon, `${o.kalanOda} oda müsait`].map((t) => (
                    <span key={t} className="rounded-full bg-zemin px-2.5 py-1 text-xs font-semibold text-lacivert">
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex min-w-[150px] flex-col items-start gap-2 md:items-end">
                <div className="flex items-baseline gap-1">
                  <span className="text-[28px] font-bold tracking-[-.03em] text-lacivert">
                    {para(o.gecelikOrtalama, o.paraBirimi)}
                  </span>
                  <span className="text-[13px] text-gri">/gece</span>
                </div>
                <span className="text-xs text-gri md:text-right">
                  {para(o.toplam, o.paraBirimi)} · {p.gece} gece, %{yuzde(p.vergiOrani)} konaklama vergisi dahil
                </span>
                <button
                  onClick={() => p.onSec(o)}
                  className="mt-1 rounded-full bg-marka-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-px hover:bg-lacivert"
                >
                  Bu odayı seç
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* --------------------------------------------------------------- adim 3 */

const girdi =
  'rounded-xl border-[1.5px] border-cizgi bg-white px-3.5 py-3 text-[15px] text-lacivert outline-none transition focus:border-marka-600 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)]';

function Bilgiler(p: {
  oda: OdaSecenegi;
  gece: number;
  giris: string;
  cikis: string;
  kisiEtiket: string;
  vergiOrani: number;
  ad: string;
  soyad: string;
  eposta: string;
  telefon: string;
  not: string;
  onlineCheckin: boolean;
  hata: string | null;
  gonderiliyor: boolean;
  setAd: (v: string) => void;
  setSoyad: (v: string) => void;
  setEposta: (v: string) => void;
  setTelefon: (v: string) => void;
  setNot: (v: string) => void;
  setOnlineCheckin: (v: boolean) => void;
  onGeri: () => void;
  onOnayla: (e: React.FormEvent) => void;
}) {
  return (
    <div className="grid items-start gap-5 [animation:rise_.45s_both] lg:grid-cols-[minmax(0,1.5fr)_minmax(280px,1fr)]">
      <form onSubmit={p.onOnayla} className="flex flex-col gap-5 rounded-[22px] border border-cizgi bg-white p-7">
        <div className="flex items-center justify-between">
          <h2 className="text-[22px] font-bold tracking-[-.02em] text-lacivert">Misafir bilgileri</h2>
          <button type="button" onClick={p.onGeri} className="text-sm font-semibold text-marka-600 hover:underline">
            ← Odalar
          </button>
        </div>

        <div className="grid gap-3.5 sm:grid-cols-2">
          <Etiketli etiket="Ad">
            <input value={p.ad} onChange={(e) => p.setAd(e.target.value)} autoComplete="given-name" className={girdi} />
          </Etiketli>
          <Etiketli etiket="Soyad">
            <input value={p.soyad} onChange={(e) => p.setSoyad(e.target.value)} autoComplete="family-name" className={girdi} />
          </Etiketli>
          <Etiketli etiket="E-posta">
            <input type="email" value={p.eposta} onChange={(e) => p.setEposta(e.target.value)} autoComplete="email" className={girdi} />
          </Etiketli>
          <Etiketli etiket="Telefon" ipucu="İsteğe bağlı">
            <input value={p.telefon} onChange={(e) => p.setTelefon(e.target.value)} autoComplete="tel" placeholder="+90 5xx xxx xx xx" className={girdi} />
          </Etiketli>
        </div>

        <label className="flex cursor-pointer items-start gap-3.5 rounded-2xl bg-mavi-zemin p-[18px]">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="#1f4fd8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none" aria-hidden="true">
            <rect x="4" y="9" width="32" height="22" rx="4" fill="#fff" />
            <circle cx="13" cy="20" r="4" />
            <path d="M22 16h10M22 21h7M22 26h9" />
            <path d="M4 12h32" stroke="#f2b632" strokeDasharray="3 3" style={{ animation: 'dash .8s linear infinite' }} />
          </svg>
          <span className="flex flex-1 flex-col gap-2">
            <span className="flex flex-wrap items-center justify-between gap-2.5">
              <span className="text-[15px] font-bold text-lacivert">Online check-in (isteğe bağlı)</span>
              <span className="text-xs font-semibold text-marka-600">Girişte sıra beklemeyin</span>
            </span>
            <span className="text-[13.5px] leading-relaxed text-gri">
              Onay ekranında size özel bir link veririz; kimliğinizi kendi telefonunuzdan okutursunuz. Görsel şifreli
              saklanır ve otelin ayarına göre en geç 24 saat sonra silinir. Yasal KBS bildiriminiz otomatik yapılır.
            </span>
            <span className="flex items-center gap-2.5">
              <input
                type="checkbox"
                checked={p.onlineCheckin}
                onChange={(e) => p.setOnlineCheckin(e.target.checked)}
                className="peer sr-only"
              />
              <span className="grid size-5 flex-none place-items-center rounded-[6px] border-[1.5px] border-cizgi bg-white transition peer-checked:border-marka-600 peer-checked:bg-marka-600">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m5 12.5 4.5 4.5L19 7" />
                </svg>
              </span>
              <span className="text-sm font-semibold text-lacivert">Kimlik yükleme linki istiyorum</span>
            </span>
          </span>
        </label>

        <Etiketli etiket="Not" ipucu="İsteğe bağlı">
          <input value={p.not} onChange={(e) => p.setNot(e.target.value)} placeholder="Geç giriş, üst kat tercihi…" className={girdi} />
        </Etiketli>

        <div className="flex flex-col gap-2.5">
          <span className="text-[13px] font-semibold text-lacivert">Ödeme</span>
          <div className="rounded-[14px] border-[1.5px] border-marka-600 bg-mavi-zemin p-3.5">
            <span className="block text-sm font-bold text-lacivert">Otelde ödeme</span>
            <span className="mt-0.5 block text-[12.5px] leading-relaxed text-gri">
              Konaklama ücreti girişte tahsil edilir. Bu sayfada kart bilgisi istenmez ve saklanmaz.
            </span>
          </div>
        </div>

        {p.hata && <p role="alert" className="text-[13px] font-medium text-kirmizi">{p.hata}</p>}

        <button
          type="submit"
          disabled={p.gonderiliyor}
          className="flex items-center justify-center gap-2.5 rounded-full bg-marka-600 py-4 text-base font-semibold text-white shadow-[0_14px_32px_-14px_rgba(31,79,216,.7)] transition hover:-translate-y-0.5 hover:bg-lacivert disabled:translate-y-0 disabled:opacity-60"
        >
          {p.gonderiliyor && (
            <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />
          )}
          {p.gonderiliyor ? 'Onaylanıyor…' : `Rezervasyonu onayla · ${para(p.oda.toplam, p.oda.paraBirimi)}`}
        </button>
        <p className="text-center text-xs leading-relaxed text-[#8a93ad]">
          Devam ederek konaklama koşullarını ve KVKK aydınlatma metnini kabul edersiniz. İptal koşulları için otelle
          iletişime geçin.
        </p>
      </form>

      <aside className="sticky top-5 flex flex-col gap-[18px] rounded-[22px] bg-lacivert p-6 text-white">
        <span className="text-xs font-bold uppercase tracking-[.1em] text-mavi-acik">Özet</span>
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-bold tracking-[-.02em]">{p.oda.ad}</h3>
          <span className="text-[13px] text-[#b6c2e2]">
            {PANSIYON_ADI[p.oda.pansiyon] ?? p.oda.pansiyon} · {p.kisiEtiket}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {(
            [
              ['Giriş', gunEtiket(p.giris), '14:00’den itibaren'],
              ['Çıkış', gunEtiket(p.cikis), '12:00’ye kadar'],
            ] as const
          ).map(([e, d, alt]) => (
            <div key={e} className="rounded-xl bg-white/[.07] p-3">
              <div className="mb-1 text-[11px] text-[#b6c2e2]">{e}</div>
              <div className="text-sm font-semibold">{d}</div>
              <div className="text-[11px] text-[#8a93ad]">{alt}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2.5 border-t border-white/[.12] pt-3.5 text-sm">
          <div className="flex justify-between">
            <span className="text-[#b6c2e2]">
              {para(p.oda.gecelikOrtalama, p.oda.paraBirimi)} × {p.gece} gece
            </span>
            <span>{para(p.oda.araToplam, p.oda.paraBirimi)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#b6c2e2]">Konaklama vergisi (%{yuzde(p.vergiOrani)})</span>
            <span>{para(p.oda.vergi, p.oda.paraBirimi)}</span>
          </div>
          <div className="flex justify-between border-t border-white/[.12] pt-3 text-lg font-bold">
            <span>Toplam</span>
            <span>{para(p.oda.toplam, p.oda.paraBirimi)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2.5 text-[12.5px] text-[#b6c2e2]">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#5fd48f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none" aria-hidden="true">
            <path d="M8 1.5l5.5 2v4c0 3.5-2.5 6-5.5 7-3-1-5.5-3.5-5.5-7v-4z" />
            <path d="M5.5 8l2 2 3-3.5" />
          </svg>
          Fiyat, otelin güncel tarifesinden alınır ve onay anında sunucuda yeniden hesaplanır.
        </div>
      </aside>
    </div>
  );
}

function Etiketli({ etiket, ipucu, children }: { etiket: string; ipucu?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between gap-2 text-[13px] font-semibold text-lacivert">
        {etiket}
        {ipucu && <span className="text-[12px] font-medium text-gri">{ipucu}</span>}
      </span>
      {children}
    </label>
  );
}

/* --------------------------------------------------------------- adim 4 */

function Onay(p: {
  sonuc: Sonuc;
  oda: OdaSecenegi;
  gece: number;
  vergiOrani: number;
  giris: string;
  cikis: string;
  eposta: string;
  tesis: Tesis | null;
  kopyalandi: boolean;
  onKopyala: () => void;
  onYeni: () => void;
}) {
  const baslik = `${p.tesis?.ad ?? 'Otel'} · ${p.oda.ad}`;
  const ics = React.useMemo(() => {
    const g = p.giris.replace(/-/g, '');
    const c = p.cikis.replace(/-/g, '');
    const satirlar = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//otelcms//rezervasyon//TR',
      'BEGIN:VEVENT',
      `UID:${p.sonuc.kod}@otelcms`,
      `DTSTART;VALUE=DATE:${g}`,
      `DTEND;VALUE=DATE:${c}`,
      `SUMMARY:${baslik}`,
      `DESCRIPTION:Rezervasyon kodu ${p.sonuc.kod}`,
      p.tesis?.adres ? `LOCATION:${p.tesis.adres}` : '',
      'END:VEVENT',
      'END:VCALENDAR',
    ].filter(Boolean);
    return `data:text/calendar;charset=utf-8,${encodeURIComponent(satirlar.join('\r\n'))}`;
  }, [p.giris, p.cikis, p.sonuc.kod, baslik, p.tesis?.adres]);

  const wa = `https://wa.me/?text=${encodeURIComponent(
    `${baslik}\nRezervasyon kodu: ${p.sonuc.kod}\n${gunEtiket(p.giris)} → ${gunEtiket(p.cikis)} (${p.gece} gece)`,
  )}`;

  return (
    <section className="grid items-center gap-9 rounded-[24px] border border-cizgi bg-white p-7 [animation:popin_.5s_cubic-bezier(.2,.8,.2,1)_both] sm:p-14 lg:grid-cols-2">
      <div className="flex flex-col gap-[18px]">
        <span className="grid size-16 place-items-center rounded-full bg-yesil-zemin">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#12a150" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M7 16.5l6 6 12-13" strokeDasharray="40" strokeDashoffset="40" style={{ animation: 'draw .6s .2s ease-out forwards' }} />
          </svg>
        </span>
        <h2 className="text-[28px] font-bold leading-[1.05] tracking-[-.03em] text-lacivert sm:text-[40px]">
          Rezervasyonunuz onaylandı
        </h2>
        <p className="text-base leading-relaxed text-gri">
          Rezervasyon kodunuzu not alın — girişte bu kodu göstermeniz yeterli.
        </p>
        <div className="inline-flex items-center gap-3 self-start rounded-[14px] bg-lacivert px-5 py-3.5 text-2xl font-bold tracking-[.12em] text-white">
          {p.sonuc.kod}
          <button
            onClick={p.onKopyala}
            className="rounded-lg bg-white/[.12] px-2.5 py-1.5 text-xs font-semibold tracking-normal transition hover:bg-white/[.22]"
          >
            {p.kopyalandi ? 'Kopyalandı' : 'Kopyala'}
          </button>
        </div>

        <div className="mt-1.5 flex flex-col gap-2.5 text-sm text-gri">
          <Satir olumlu>
            {p.oda.ad} · {gunEtiket(p.giris)} → {gunEtiket(p.cikis)} · {p.gece} gece
          </Satir>
          <Satir olumlu>
            Ödeme otelde · {para(p.sonuc.toplam, p.sonuc.paraBirimi)} (%{yuzde(p.vergiOrani)} konaklama
            vergisi dahil)
          </Satir>
          <Satir olumlu={!!p.sonuc.onlineCheckinUrl}>
            {p.sonuc.onlineCheckinUrl
              ? 'Online check-in linkiniz hazır — kimliğinizi şimdi yükleyebilirsiniz.'
              : 'Kimliğinizi girişte gösterin; KBS bildiriminiz resepsiyonda yapılır.'}
          </Satir>
        </div>

        {/* E-posta gonderimi henuz bagli degil; "gonderildi" demek yerine
            misafire kodu ve linkleri burada veriyoruz. */}
        <p className="rounded-xl bg-zemin px-3.5 py-3 text-[13px] leading-relaxed text-gri">
          {p.sonuc.epostaGonderiliyor ? (
            <>
              Onay e-postası <strong className="text-lacivert">{p.eposta}</strong> adresine gönderildi. Birkaç dakika
              içinde gelmezse istenmeyen posta klasörünü kontrol edin.
            </>
          ) : (
            <>
              Kaydınız <strong className="text-lacivert">{p.eposta}</strong> adresiyle oluşturuldu. Bu tesiste otomatik
              onay e-postası henüz açık değil; bu ekranı kaydedin ya da kodu bir yere not alın.
            </>
          )}
          {p.tesis?.telefon && <> Soru için: <strong className="text-lacivert">{p.tesis.telefon}</strong></>}
        </p>

        <div className="mt-1.5 flex flex-wrap gap-2.5">
          {p.sonuc.onlineCheckinUrl && (
            <a
              href={p.sonuc.onlineCheckinUrl}
              className="rounded-full bg-marka-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-lacivert"
            >
              Kimliğimi şimdi yükle
            </a>
          )}
          <a
            href={ics}
            download={`${p.sonuc.kod}.ics`}
            className="rounded-full border-[1.5px] border-lacivert px-5 py-3 text-sm font-semibold text-lacivert transition hover:bg-mavi-zemin"
          >
            Takvime ekle
          </a>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border-[1.5px] border-cizgi px-5 py-3 text-sm font-semibold text-lacivert transition hover:border-lacivert"
          >
            WhatsApp ile gönder
          </a>
          <button onClick={p.onYeni} className="px-2 py-3 text-sm font-semibold text-gri transition hover:text-lacivert">
            Yeni rezervasyon
          </button>
        </div>
      </div>

      <KartCizimi kod={p.sonuc.kod} tesisAdi={p.tesis?.ad ?? ''} oda={p.oda.ad} gece={p.gece} checkinHazir={!!p.sonuc.onlineCheckinUrl} />
    </section>
  );
}

function Satir({ olumlu, children }: { olumlu: boolean; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        className="grid size-[22px] flex-none place-items-center rounded-full text-xs font-bold"
        style={olumlu ? { background: '#e6f7ec', color: '#12a150' } : { background: '#fff5dc', color: '#b9840f' }}
      >
        {olumlu ? '✓' : '!'}
      </span>
      <span>{children}</span>
    </div>
  );
}

function KartCizimi({ kod, tesisAdi, oda, gece, checkinHazir }: { kod: string; tesisAdi: string; oda: string; gece: number; checkinHazir: boolean }) {
  return (
    <svg viewBox="0 0 360 300" className="w-full max-w-[380px] justify-self-center" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <g style={{ animation: 'bob 5s ease-in-out infinite' }}>
        <rect x="60" y="40" width="240" height="150" rx="18" fill="#0b1530" />
        <text x="84" y="76" fontSize="11" fontWeight="700" fill="#8fb0ff" letterSpacing="1.5">
          {tesisAdi.toLocaleUpperCase('tr-TR').slice(0, 22)}
        </text>
        <text x="84" y="110" fontSize="17" fontWeight="700" fill="#fff" letterSpacing="1">
          {kod}
        </text>
        <text x="84" y="134" fontSize="11" fill="#b6c2e2">
          {oda.slice(0, 28)} · {gece} gece
        </text>
        {checkinHazir && (
          <>
            <rect x="84" y="150" width="150" height="18" rx="9" fill="#1f4fd8" />
            <text x="159" y="163" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff">
              ONLINE CHECK-IN HAZIR
            </text>
          </>
        )}
        <g stroke="#fff" strokeWidth="2">
          <path d="M258 62h24v24M282 62l-24 24" />
        </g>
      </g>
      <g stroke="#1f4fd8" strokeWidth="2" style={{ animation: 'bob 6s 1s ease-in-out infinite' }}>
        <circle cx="90" cy="250" r="16" />
        <circle cx="90" cy="250" r="6" />
        <path d="M106 250h70M154 250v12M168 250v9" />
      </g>
      <text x="200" y="256" fontSize="12" fontWeight="600" fill="#5b6480">
        Oda girişte hazır
      </text>
      <circle cx="330" cy="30" r="4" fill="#f2b632" style={{ animation: 'blink 1.4s infinite' }} />
      <circle cx="40" cy="60" r="3" fill="#8fb0ff" style={{ animation: 'blink 2s .4s infinite' }} />
    </svg>
  );
}
